import {
  FieldValues,
  FormProvider,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import { SessionsAndEventsResponse } from "../../../../api/services/program/models";
import { ITabs, MuiTabPanel, MuiTabs } from "../../../../components";
import { PAGE_IDS } from "../../../../helpers/constants";
import { UserDetails } from "../../../../helpers/types/common";
import { getValueOrBlankArray } from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { CreateProgramTabsWrapper } from "../../../createProgram/components/createProgramTabs/CreateProgramTabs.style";
import { DocumentsTab } from "../../../createProgram/components/createProgramTabs/tabs/documents/DocumentsTab";
import { ParticipantTab } from "../../../createProgram/components/createProgramTabs/tabs/participant/ParticipantTab";
import { ProgramTab } from "../../../createProgram/components/createProgramTabs/tabs/program/ProgramTab";
import {
  ParticipantDetail,
  ParticipantTabFormType,
  ProgramDetail,
  ProgramTabFormHookType,
} from "../../../createProgram/modelsAndSchemas/Models";
import { programDocumentsTabSiteCoreKeys } from "../../../createProgram/siteCore/SiteCoreKeys";
import { CPParticipantActivityTimeline } from "../../components/cpParticipantActivityTimeline/CPParticipantActivityTimeline";
import { ManageParticipantsSessionAndEventsTab } from "../../components/manageParticipantsSessionAndEventsTab/ManageParticipantsSessionAndEventsTab";
import {
  CATEGORY_FILTER_KEYS,
  ManageParticipantsSessionAndEventsFilterFormKeys,
  TYPE_FILTER_KEYS,
  UPCOMING_PERIOD_FILTER_KEYS,
} from "../../modelsAndSchemas/Models";
import { manageParticipantHeaderSiteCoreKeys } from "../../sitecore/SiteCoreKeys";

const pageId = PAGE_IDS.COACH_PORTAL_EDIT_PARTICIPANT;

interface CPEditParticipantProps {
  programDetail?: ProgramDetail;
  participantDetail?: ParticipantDetail;
  sessionsEventList?: SessionsAndEventsResponse[];
  handleUpdateModifyNextContactDateClick: () => void;
  selectedTabIndex: number;
  setShowModifyNextContactDateModal: (value: boolean) => void;
  showModifyNextContactDateModal: boolean;
  clientRelationshipOwnerList: UserDetails[];
  programMethods: UseFormReturn<ProgramTabFormHookType, any, undefined>;
  participantMethods: UseFormReturn<ParticipantTabFormType, any, undefined>;
  participantActivityTimelineMethods: UseFormReturn<
    FieldValues,
    any,
    undefined
  >;
  onTabChange: (event: React.ChangeEvent<{}>, value: number) => void;
}

export const CPEditParticipant = ({
  programDetail,
  participantDetail,
  sessionsEventList,
  handleUpdateModifyNextContactDateClick,
  selectedTabIndex,
  setShowModifyNextContactDateModal,
  showModifyNextContactDateModal,
  clientRelationshipOwnerList,
  participantActivityTimelineMethods,
  programMethods,
  participantMethods,
  onTabChange,
}: CPEditParticipantProps) => {
  const { id: programId = "" } = useParams();
  const { useFetchLabel, fetchComponentTitle } = useSiteCore();

  const sessionsAndEventsFormDefaultValues = {
    [ManageParticipantsSessionAndEventsFilterFormKeys.type]:
      TYPE_FILTER_KEYS.UPCOMING_SESSIONS_AND_EVENTS,
    [ManageParticipantsSessionAndEventsFilterFormKeys.period]:
      UPCOMING_PERIOD_FILTER_KEYS.NEXT_7_DAYS,
    [ManageParticipantsSessionAndEventsFilterFormKeys.category]:
      CATEGORY_FILTER_KEYS.ALL,
  };

  const sessionsAndEventsMethods = useForm({
    mode: "all",
    defaultValues: sessionsAndEventsFormDefaultValues,
  });

  const {
    manageParticipantHeaderTitle,
    programTab,
    participantTab,
    activityTimelineTab,
    sessionsAndEventsTab,
    documentsTab,
  } = manageParticipantHeaderSiteCoreKeys;

  const getLabel = useFetchLabel(manageParticipantHeaderTitle);

  const tabIdProgramTab = `${getLabel(programTab)}-tab`;
  const tabIdParticipantTab = `${getLabel(participantTab)}-tab`;
  const tabIdDocuments = `${getLabel(documentsTab)}-tab`;
  const tabIdActivityTimelineTab = `${getLabel(activityTimelineTab)}-tab`;
  const tabIdSessionAndEventsTab = `${getLabel(sessionsAndEventsTab)}-tab`;

  const EDIT_PARTICIPANTS_TABS: ITabs[] = [
    {
      id: tabIdProgramTab,
      label: getLabel(programTab),
      content: (
        <FormProvider {...programMethods}>
          <ProgramTab
            pageId={pageId}
            clientRelationshipOwnerList={getValueOrBlankArray(
              clientRelationshipOwnerList,
            )}
            programDetail={programDetail}
            programId={programId}
            isEditProgram
            isFromCoachPortal
          />
        </FormProvider>
      ),
    },
    {
      id: tabIdParticipantTab,
      label: getLabel(participantTab),
      content: (
        <FormProvider {...participantMethods}>
          <ParticipantTab
            pageId={pageId}
            programDetail={programDetail}
            programId={programId}
            isEditProgram
            isFromCoachPortal
          />
        </FormProvider>
      ),
    },
    {
      id: tabIdDocuments,
      label: fetchComponentTitle(
        programDocumentsTabSiteCoreKeys.documentsTitle,
      ),
      content: (
        <DocumentsTab pageId={pageId} programId={programId} isFromCoachPortal />
      ),
    },
    {
      id: tabIdActivityTimelineTab,
      label: getLabel(activityTimelineTab),
      content: (
        <CPParticipantActivityTimeline
          programId={programId}
          showModifyNextContactDateModal={showModifyNextContactDateModal}
          handleUpdateModifyNextContactDateClick={
            handleUpdateModifyNextContactDateClick
          }
          setShowModifyNextContactDateModal={setShowModifyNextContactDateModal}
          participantDetail={participantDetail}
          displayAddBtn
          isEditProgram
          participantActivityTimelineMethods={
            participantActivityTimelineMethods
          }
        />
      ),
    },
    {
      id: tabIdSessionAndEventsTab,
      label: getLabel(sessionsAndEventsTab),
      content: (
        <FormProvider {...sessionsAndEventsMethods}>
          <ManageParticipantsSessionAndEventsTab
            sessionsEventList={sessionsEventList}
          />
        </FormProvider>
      ),
    },
  ];

  const currentTab = EDIT_PARTICIPANTS_TABS[selectedTabIndex];
  // this help in rerendering the component when the content is a function component, as MuiTabPanel only re-render when the value changes, not when the content changes
  const currentTabContent =
    typeof currentTab?.content === "function"
      ? currentTab.content()
      : currentTab?.content;

  return (
    <CreateProgramTabsWrapper data-testid="cp-edit-participant-main-wrapper">
      <MuiTabs
        tabs={EDIT_PARTICIPANTS_TABS}
        value={selectedTabIndex}
        onChange={onTabChange}
        tabsTestIds={EDIT_PARTICIPANTS_TABS.map(
          (_, index) => `${pageId}-tab${index + 1}`,
        )}
        className="program-participant-tab"
        data-testid="cp-edit-participant-tabs"
      >
        <MuiTabPanel
          panelId={`${pageId}-tab${selectedTabIndex + 1}`}
          value={selectedTabIndex}
          index={selectedTabIndex}
          isPanelWithMount
          ariaLabelledById={EDIT_PARTICIPANTS_TABS[selectedTabIndex]?.id}
          data-testid={`cp-edit-participant-tab-panel-${selectedTabIndex}`}
        >
          {currentTabContent}
        </MuiTabPanel>
      </MuiTabs>
    </CreateProgramTabsWrapper>
  );
};
